import { useState, useEffect, useRef } from 'react'
import { useSessionStore, useUIStore } from '@stores'
import { MessageDisplayTime, ProcessingAction, getCombinedActionMessages } from '@constants/loadingMessages'

export interface ProcessingGifProps {
  className?: string
  processing?: boolean
  useActionMessages?: boolean
}

interface ProcessingMessage {
  customMessages?: string[]
  actions?: ProcessingAction[]
}

// Background pixels = transparent OR very close to white. We crop these away
// so every brand's spinner renders at roughly the same visible size regardless
// of how much padding the source GIF was authored with.
const isBackgroundPixel = (r: number, g: number, b: number, a: number) => {
  if (a < 16) {
    return true
  }
  return r > 240 && g > 240 && b > 240
}

const CONTENT_PADDING = 0.6

const isSameOriginUrl = (url: string): boolean => {
  if (!url) {
    return false
  }
  if (url.startsWith('/') || url.startsWith('./') || url.startsWith('../')) {
    return true
  }
  try {
    return new URL(url, window.location.href).origin === window.location.origin
  } catch {
    return false
  }
}

const ProcessingGif = ({
  className = 'processing-image',
  processing,
  useActionMessages = true,
}: ProcessingGifProps) => {
  const [currentProcessingMessage, setCurrentProcessingMessage] = useState<string | null>(null)
  const [contentTransform, setContentTransform] = useState<string | undefined>(undefined)
  const timerRef = useRef<NodeJS.Timeout | null>(null)
  const imgRef = useRef<HTMLImageElement | null>(null)

  const { isLoading } = useUIStore()
  const { session } = useSessionStore()

  // Determine if we should show loading
  const showLoading = processing !== undefined ? processing : isLoading

  // Get processing GIF URL from campaign data
  const processingGIF = session?.campaign?.processing_gif?.file || null

  // Reset auto-crop transform whenever the GIF source changes — different
  // brands ship different padding profiles, so we must re-detect each time.
  useEffect(() => {
    setContentTransform(undefined)
  }, [processingGIF])

  // Clear timer and message when loading stops
  useEffect(() => {
    if (!showLoading) {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
      setCurrentProcessingMessage(null)
    }
  }, [showLoading])

  // Handle processing message events
  useEffect(() => {
    const handleProcessingMessages = (event: CustomEvent<ProcessingMessage>) => {
      const { customMessages, actions } = event.detail

      // Clear existing timer
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }

      let messages = customMessages || []

      // Add action messages if enabled
      if (useActionMessages && actions && actions.length) {
        messages = messages.concat(getCombinedActionMessages(actions))
      }

      if (messages.length) {
        displayMessages(messages)
      } else {
        setCurrentProcessingMessage(null)
      }
    }

    // Listen for custom events
    window.addEventListener('toggle-processing-gifs', handleProcessingMessages as EventListener)

    return () => {
      window.removeEventListener('toggle-processing-gifs', handleProcessingMessages as EventListener)
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
    }
  }, [useActionMessages])

  const displayMessages = (messages: string[]) => {
    const displayMessage = (index: number) => {
      if (index < messages.length) {
        setCurrentProcessingMessage(messages[index])

        if (index + 1 < messages.length) {
          timerRef.current = setTimeout(() => {
            displayMessage(index + 1)
          }, MessageDisplayTime)
        }
      }
    }

    displayMessage(0)
  }

  // Once the image loads, sample its pixels to find the bounding box of the
  // actual spinner (i.e. discard any transparent / near-white padding around
  // it), then compute a CSS transform that scales + recenters the GIF so its
  // visible content fills the wrapper. The image element itself isn't
  // resized; the transform happens entirely in CSS and the wrapper clips the
  // overflow. Works on animated GIFs (the bbox is stable across frames for
  // typical spinner animations) and degrades gracefully if the canvas read
  // fails (e.g. CORS-tainted) — the GIF just renders at its natural ratio.
  const handleImgLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget
    const w = img.naturalWidth
    const h = img.naturalHeight
    if (!w || !h) {
      return
    }

    try {
      const canvas = document.createElement('canvas')
      canvas.width = w
      canvas.height = h
      const ctx = canvas.getContext('2d', { willReadFrequently: true })
      if (!ctx) {
        return
      }
      ctx.drawImage(img, 0, 0)
      const data = ctx.getImageData(0, 0, w, h).data

      // Scan every 2px to keep this cheap for large GIFs.
      const step = Math.max(1, Math.floor(Math.min(w, h) / 200))
      let minX = w
      let minY = h
      let maxX = -1
      let maxY = -1
      for (let y = 0; y < h; y += step) {
        for (let x = 0; x < w; x += step) {
          const i = (y * w + x) * 4
          if (!isBackgroundPixel(data[i], data[i + 1], data[i + 2], data[i + 3])) {
            if (x < minX) {
              minX = x
            }
            if (x > maxX) {
              maxX = x
            }
            if (y < minY) {
              minY = y
            }
            if (y > maxY) {
              maxY = y
            }
          }
        }
      }

      if (maxX < 0) {
        return // entire frame is background — leave untouched
      }

      const bboxW = maxX - minX + 1
      const bboxH = maxY - minY + 1
      // Always compute a scale — even tightly-cropped GIFs get scaled DOWN to
      // hit the target fill, so brands with no whitespace and brands with lots
      // of whitespace converge on the same visible spinner size.
      const fitScale = Math.min(w / bboxW, h / bboxH)
      const scale = fitScale * CONTENT_PADDING
      const bboxCx = (minX + maxX) / 2
      const bboxCy = (minY + maxY) / 2
      // translate(X%, Y%) uses the element's *pre-transform* size. Combined
      // with scale around the default (center) transform-origin, this shifts
      // the bbox's display center onto the wrapper's center.
      const pctX = 100 * scale * (0.5 - bboxCx / w)
      const pctY = 100 * scale * (0.5 - bboxCy / h)
      setContentTransform(`translate(${pctX.toFixed(2)}%, ${pctY.toFixed(2)}%) scale(${scale.toFixed(3)})`)
    } catch {
      // Canvas read failed (likely CORS-tainted). Fine — leave the GIF as-is.
    }
  }

  // Don't render if not loading or no processing GIF
  if (!showLoading || !processingGIF) {
    return null
  }

  return (
    <div className="flex flex-col items-center justify-center text-center">
      <div className={`${className} processing-gif-fit-frame`}>
        <img
          ref={imgRef}
          alt="Processing..."
          crossOrigin={isSameOriginUrl(processingGIF) ? 'anonymous' : undefined}
          src={processingGIF}
          onLoad={handleImgLoad}
          onError={e => {
            ;(e.target as HTMLImageElement).src = '/assets/icons/loader.svg'
          }}
          style={{
            transform: contentTransform,
            transformOrigin: 'center center',
            transition: 'transform 200ms ease-out',
          }}
        />
      </div>
      {currentProcessingMessage && <h4 className="processing-gif-message">{currentProcessingMessage}</h4>}
    </div>
  )
}

export default ProcessingGif
